/**
 * Человеческие формулировки причин готовности карточки МП.
 *
 * Коды приходят с бэкенда как есть — их считает `marketplace-card.service`
 * при заморозке черновика и теми же кодами отвечает адаптер публикации.
 * Переводим их здесь, а не в сервисе, по той же причине, по какой сервис их не
 * переводит: имя причины не должно меняться по дороге, иначе её не найти ни в
 * логе, ни в ответе площадки.
 *
 * Незнакомый код — не повод молчать: показываем его как есть, чтобы новая
 * причина с бэкенда была видна человеку, а не проглатывалась фронтом.
 */

export interface ReasonText {
  /** Что произошло — одной строкой. */
  title: string
  /** Почему это важно; пустая строка, если добавить нечего. */
  detail: string
}

/** Причины, блокирующие публикацию: с ними карточка на площадку не уедет. */
const BLOCKING: Record<string, ReasonText> = {
  MARKETPLACE_PRICE_MISSING: {
    title: 'В справочнике категории не заведена цена для этой площадки',
    detail: 'Карточка без цены не создаётся: OZON требует цену сразу при заведении товара.',
  },
  OZON_CATEGORY_MISSING: {
    title: 'Не заполнена пара «категория + тип» OZON',
    detail: 'Без неё товар не завести — это аналог предмета у Wildberries.',
  },
  MARKETPLACE_SUBJECT_MISSING: {
    title: 'У категории PIM не задан предмет Wildberries',
    detail: 'Предмет определяет набор обязательных характеристик карточки.',
  },
  SEO_REVISION_MISSING: {
    title: 'Нет ссылки на утверждённую SEO-ревизию',
    detail: 'Публикация всегда ссылается на конкретную ревизию текста.',
  },
  SEO_TEXT_MISSING: {
    title: 'Не заполнено наименование или описание',
    detail: 'Оба поля обязательны для обеих площадок.',
  },
  PIPELINE_VISUALS_MISSING: {
    title: 'Конвейер не сделал ни одного визуала',
    detail: 'Публиковать нечего: запустите генерацию визуалов и повторите шаг.',
  },
  PIPELINE_VISUALS_CABINET_MISSING: {
    title: 'Нет визуалов для кабинета этой карточки',
    detail: 'Визуалы товара есть, но сняты для другого кабинета — площадке они не подойдут.',
  },
  PIPELINE_VISUALS_NOT_PUBLISHED: {
    title: 'Визуалы остались на локальных путях',
    detail: 'Площадка не скачает ссылку, доступную только внутри сети.',
  },
  FROZEN_SNAPSHOT_SECTION_MISSING: {
    title: 'В замороженном составе нет обязательного раздела',
    detail: 'Пересоберите черновик карточки.',
  },
  YM_CATEGORY_MISSING: {
    title: 'У категории ПИМ не выбрана категория Маркета',
    detail: 'Заведите соответствие на вкладке «Настройки Я.Маркет» (base_categories.ym_category_id).',
  },
  YM_DICTIONARY_VALUE_MISSING: {
    title: 'Значения нет в справочнике характеристики категории Маркета',
    detail: 'Свободный ввод для этого поля у площадки выключен (allowCustomValues=false).',
  },
  YM_WRITE_DISABLED: {
    title: 'Запись на Маркет выключена',
    detail: 'Предохранитель транспорта YM_WRITE_ENABLED: первая боевая отправка — только по согласованию владельца.',
  },
  // Отказы сборщика тела отправки (окно черновика ПИМ): коды рождаются в
  // `frozen-card-revision.service.ts` и `marketplace-card-payload-preview.service.ts`.
  SEO_REVISION_MISMATCH: {
    title: 'Замороженная ссылка на текст указывает не на ту ревизию',
    detail: 'После заморозки контент пересобрали. Карточку нужно заново подготовить к публикации.',
  },
  PAYLOAD_EMPTY: {
    title: 'Сборщик не отдал тело отправки',
    detail: 'Обычно это значит, что у карточки нет утверждённого текста или не хватает обязательных данных товара.',
  },
  MARKETPLACE_CARD_REJECTED: {
    title: 'Площадка отклонила карточку и назвала причину',
    detail: 'Причина — в исходном сообщении площадки рядом.',
  },
}

/** Пробелы: карточка уедет, но поле на площадке останется пустым. */
const GAPS: Record<string, ReasonText> = {
  tnved: {
    title: 'Не заполнен код ТН ВЭД',
    detail: 'Карточка уедет, но поле на площадке останется пустым.',
  },
  weight_gross: {
    title: 'Не указан вес брутто',
    detail: 'Площадка запросит вес упаковки при первом же обновлении карточки.',
  },
  declaration_number: {
    title: 'Не заполнен номер декларации соответствия',
    detail: 'Для пищевого товара площадка может ограничить показ карточки.',
  },
}

/**
 * Часть кодов приходит с уточнением через двоеточие:
 * `SEO_REVISION_MISSING: content_id, revision_id`. Основа кода — до двоеточия,
 * хвост показываем как есть: он называет конкретные поля.
 */
function split(code: string): { base: string; tail: string } {
  const index = code.indexOf(':')
  return index === -1
    ? { base: code.trim(), tail: '' }
    : { base: code.slice(0, index).trim(), tail: code.slice(index + 1).trim() }
}

function resolve(code: string, dictionary: Record<string, ReasonText>): ReasonText {
  const { base, tail } = split(code)
  const known = dictionary[base]
  if (!known) return { title: code, detail: '' }
  return tail ? { title: `${known.title}: ${tail}`, detail: known.detail } : known
}

export const blockingReason = (code: string): ReasonText => resolve(code, BLOCKING)
export const gapReason = (code: string): ReasonText => resolve(code, GAPS)

/**
 * Одна фраза-подсказка по коду отказа («Название. Пояснение.») — для окон, где
 * под кодом нужен связный текст, а не заголовок с пояснением. Код, которого нет
 * в словаре блокировок, даёт `null`: окно покажет исходное сообщение площадки.
 */
export function refusalHint(code: string | null | undefined): string | null {
  const text = String(code ?? '').trim()
  if (!text) return null
  const { base } = split(text)
  if (!(base in BLOCKING)) return null
  const reason = blockingReason(text)
  return reason.detail ? `${reason.title}. ${reason.detail}` : reason.title
}

/**
 * Ответ площадки приходит на английском и попадает в `error` карточки. Переводим
 * только те формулировки, что встречались в боевых данных; остальные показываем
 * как есть, ниже оригинала.
 */
export function marketplaceErrorHint(error: string | null | undefined): string | null {
  const text = String(error || '').trim()
  if (!text) return null
  if (/newitem price/i.test(text)) {
    return 'В справочнике категории не заведена цена новинки для этой площадки.'
  }
  return null
}

/**
 * Краткая формулировка причины для сгруппированного списка («Маркет: у категории
 * не задана категория Яндекс Маркета — 8 товаров») и подсказка, что делать.
 * Полный текст причины остаётся в `blockingReason` — здесь короткая фраза без
 * заглавной буквы, которую можно поставить после названия площадки.
 */
export interface ReasonSummary {
  /** Что не так — фразой со строчной буквы, без точки. */
  short: string
  /** Что делать; null, если добавить нечего. */
  action: string | null
}

const SUMMARY: Record<string, ReasonSummary> = {
  YM_CATEGORY_MISSING: {
    short: 'у категории не задана категория Яндекс Маркета',
    action: 'Откройте справочник категорий, выберите категорию Яндекс Маркета и нажмите «Повторить шаг».',
  },
  YM_DICTIONARY_VALUE_MISSING: {
    short: 'значения нет в справочнике характеристики Яндекс Маркета',
    action: 'Выберите значение из справочника площадки в характеристиках категории.',
  },
  YM_WRITE_DISABLED: {
    short: 'запись на Яндекс Маркет выключена',
    action: null,
  },
  OZON_CATEGORY_MISSING: {
    short: 'у категории не заполнена пара «категория + тип» OZON',
    action: 'Заполните пару в справочнике категорий и нажмите «Повторить шаг».',
  },
  MARKETPLACE_SUBJECT_MISSING: {
    short: 'у категории не задан предмет Wildberries',
    action: 'Укажите предмет Wildberries в справочнике категорий и нажмите «Повторить шаг».',
  },
  MARKETPLACE_PRICE_MISSING: {
    short: 'в справочнике категории нет цены для площадки',
    action: 'Задайте цену категории в модуле ценообразования и нажмите «Повторить шаг».',
  },
  SEO_REVISION_MISSING: {
    short: 'нет утверждённой ревизии SEO-текста',
    action: 'Сгенерируйте тексты заново: меню «Ещё» → «Перегенерировать тексты SEO».',
  },
  SEO_TEXT_MISSING: {
    short: 'не заполнено наименование или описание',
    action: 'Сгенерируйте тексты заново: меню «Ещё» → «Перегенерировать тексты SEO».',
  },
  SEO_PACKAGE_MISSING: {
    short: 'для товара не собран SEO-пакет',
    action: 'Сгенерируйте тексты заново: меню «Ещё» → «Перегенерировать тексты SEO».',
  },
  PIPELINE_VISUALS_MISSING: {
    short: 'нет ни одного визуала',
    action: 'Запустите генерацию визуалов и нажмите «Повторить шаг».',
  },
  PIPELINE_VISUALS_CABINET_MISSING: {
    short: 'визуалы сняты для другого кабинета',
    action: 'Перегенерируйте визуалы для нужных кабинетов: меню «Ещё» → «Перегенерировать визуалы».',
  },
  PIPELINE_VISUALS_NOT_PUBLISHED: {
    short: 'визуалы созданы, но не сохранены в публичный каталог — площадка не сможет их скачать',
    action: 'Проверьте доступность публичного каталога и нажмите «Повторить шаг».',
  },
  FROZEN_SNAPSHOT_SECTION_MISSING: {
    short: 'в замороженном составе карточки нет обязательного раздела',
    action: 'Пересоберите черновик карточки.',
  },
  DRAFT_READINESS_MISSING: {
    short: 'не удалось оценить готовность карточки',
    action: 'Нажмите «Повторить шаг»; если ошибка вернётся, откройте техническую информацию ниже.',
  },
}

/** Краткая формулировка и подсказка по коду; незнакомый код возвращается как есть. */
export function reasonSummary(code: string): ReasonSummary {
  const { base } = split(code)
  return SUMMARY[base] ?? { short: blockingReason(code).title, action: null }
}

/** Код известен словарю блокировок или кратких причин. */
export const isKnownReasonCode = (code: string): boolean => {
  const { base } = split(code)
  return base in SUMMARY || base in BLOCKING || base in GAPS
}
